import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectPartnersOrderState, selectIsValid } from '@store/partners-order/selectors';
import { setShowInvalids, resetStore } from '@store/partners-order/slice';
import { Button } from 'react-aria-components';
import { useSavePartnerOrderMutation } from '@api/baseApi';
import { convertToPartnerOrderDef, convertToPartnerOrderPurchase } from '@store/partners-order/convertors';
import { NavigateRoutes } from '@features/routing/routesObject';
import { purchase } from '@features/gtm/gtmEvents';
import LoadingSpinner from '@components/loading-spinner/LoadingSpinner';



interface SaveButtonProps {
	scrollToTop: () => void
}


const SaveButton = (props: SaveButtonProps) => {
	const { scrollToTop } = props;
	const { t } = useTranslation([ 'forms', 'global' ]);
	
	const partnersOrderState = useSelector(selectPartnersOrderState);
	const isValid = useSelector(selectIsValid);
	const navigate = useNavigate();
	
	const dispatch = useDispatch();
	const showInvalids = (show: boolean) => dispatch(setShowInvalids(show));
	const runResetStore = () => dispatch(resetStore());
	const [ savePartnerOrder, { isError, isLoading, isSuccess } ] = useSavePartnerOrderMutation();


	const handleSave = useCallback(() => {
		if (!isValid || !partnersOrderState) {
			showInvalids(true);
			setTimeout(() => scrollToTop(), 5);
			return;
		}
		
		let convertedObject = convertToPartnerOrderDef(partnersOrderState);
		if (!convertedObject) return;
		
		savePartnerOrder(convertedObject);
	},[ isValid, partnersOrderState, scrollToTop ]);


	useEffect(() => {
		if (isSuccess) {
			let convertedGtmObject = convertToPartnerOrderPurchase(partnersOrderState);
			purchase(convertedGtmObject);
			runResetStore();
			navigate(NavigateRoutes.thankYouOrder);
		}
	}, [ isSuccess ]);


	if (isError) return (
		<p className='error-message'>{t('postErrorMessage', { ns: 'global' })}</p>
	)


	return (
		<Button className='app-form-button disable-selection' autoFocus={false} isDisabled={isLoading} onPress={handleSave}>
			{isLoading ? <LoadingSpinner /> : t('footer.finishOrder')}
		</Button>
	);
};

export default memo(SaveButton);